export interface PlaceDetails {
  city: string;
  state: string;
  zipcode: string;
  country: string;
  formattedAddress: string;
}

export interface PlacePrediction {
  description: string;
  placeID: string;
  primaryText: string;
  secondaryText: string;
  types: string[];
  distanceMeters: number;
}

/**
 * See https://developers.google.com/maps/documentation/geocoding/requests-geocoding
 * for details about address component types
 */
enum GooglePlaceKeys {
  CITY = 'locality',
  CITY_BACKUP = 'sublocality',
  STATE = 'administrative_area_level_1',
  POSTAL_CODE = 'postal_code',
  COUNTRY = 'country',
  STREET_NUMBER = 'street_number',
  ROUTE = 'route',
}

/**
 * Fetches detailed place information from Google Places API using a place ID
 * @param placeID - The Google Places place ID
 * @returns Promise<PlaceDetails | undefined> - Parsed place details or undefined if error
 */
export const fetchPlaceDetails = async (placeID: string): Promise<PlaceDetails | undefined> => {
  try {
    const GooglePlacesSDK = require('react-native-google-places-sdk').default;
    const placeDetails = await GooglePlacesSDK.fetchPlaceByID(placeID);

    if (!placeDetails) {
      return undefined;
    }

    let city = '';
    let state = '';
    let zipcode = '';
    let country = '';

    if (placeDetails.addressComponents) {
      for (const component of placeDetails.addressComponents) {
        const types = component.types || [];

        if (types.includes(GooglePlaceKeys.CITY)) {
          city = component.name || '';
        } else if (types.includes(GooglePlaceKeys.CITY_BACKUP) && !city) {
          city = component.name || '';
        } else if (types.includes(GooglePlaceKeys.STATE)) {
          state = component.shortName || component.name || '';
        } else if (types.includes(GooglePlaceKeys.POSTAL_CODE)) {
          zipcode = component.name || '';
        } else if (types.includes(GooglePlaceKeys.COUNTRY)) {
          country = component.name || '';
        }
      }
    }

    return {
      city,
      state,
      zipcode,
      country,
      formattedAddress: placeDetails.formattedAddress || '',
    };
  } catch (error) {
    console.error('Error fetching place details:', error);
    return undefined;
  }
};

/**
 * Fetches place predictions from Google Places API for autocomplete
 * @param text - The search text (minimum 2 characters)
 * @param types - Array of place types to search for (default: ['geocode'])
 * @returns Promise<PlacePrediction[]> - Array of place predictions
 */
export const fetchPlacePredictions = async (
  text: string,
  types: string[] = ['geocode'],
): Promise<PlacePrediction[]> => {
  try {
    if (!text || text.length < 2) {
      return [];
    }

    const GooglePlacesSDK = require('react-native-google-places-sdk').default;
    const predictions = await GooglePlacesSDK.fetchPredictions(text, { types });

    if (!predictions || predictions.length === 0) {
      return [];
    }

    const usPredictions = predictions.filter((prediction: any) => {
      const description = prediction.description || '';
      const secondaryText = prediction.secondaryText || '';
      return description.includes('USA') || secondaryText.includes('USA');
    });

    return usPredictions.map((prediction: any) => {
      let secondaryText = prediction.secondaryText || '';

      if (secondaryText.includes(', USA')) {
        secondaryText = secondaryText.replace(', USA', '');
      }

      return {
        description: prediction.description,
        placeID: prediction.placeID,
        primaryText: prediction.primaryText || prediction.description,
        secondaryText: secondaryText,
        types: prediction.types || [],
        distanceMeters: prediction.distanceMeters || 0,
      };
    });
  } catch (error) {
    console.error('Error fetching place predictions:', error);
    return [];
  }
};

/**
 * Helper function to check if a place is in the United States
 * @param prediction - Place prediction object
 * @returns boolean - True if the place is in the USA
 */
export const isUSPlace = (prediction: PlacePrediction): boolean => {
  const description = prediction.description || '';
  const secondaryText = prediction.secondaryText || '';
  return description.includes('USA') || secondaryText.includes('USA');
};

/**
 * Formats a place prediction for display
 * @param prediction - Place prediction object
 * @returns string - Formatted display text
 */
export const formatPlaceDisplay = (prediction: PlacePrediction): string => {
  if (prediction.secondaryText) {
    return `${prediction.primaryText}, ${prediction.secondaryText}`;
  }
  return prediction.primaryText;
};
